import {Spec} from 'immutability-helper';
import React, {UpdateSpec} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import CaptionEditableClass from 'notebook/mediocre/base-plugin/CaptionEditable';
import {createCollapseId} from 'notebook/mediocre/base-plugin/createCollapseId';
import MediocrePlugins, {TEXT_PLUGIN_COMPONENT_BASE} from 'notebook/mediocre/base-plugin/plugins';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {
  buildTableSearchResult,
  doesTableCellMatch,
  getSearchResultsForColumnNames,
  getSearchResultsForTableName,
} from 'notebook/mediocre/search/TableSearchUtil';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {Table} from 'notebook/tables/constants/TableConstants';
import TableSkeleton, {getTableSkeletonHeight} from 'notebook/tables/core-ui/TableSkeleton';
import {OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {isTableCollapsed} from 'notebook/tables/store/logic/TableVisibilityLogic';
import TableUtil from 'notebook/tables/TableUtil';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import pick from 'util/pick';

import TEXT_PLUGIN from '../text-block/TextPlugin';

interface TableContent {
  text: string;
  table: Table;
  alignment?: 'left' | 'right' | 'center' | null;
  formats?: Format;
}

const TABLE_CAPTION_LINE_HEIGHT = 19;

const TABLE_PLUGIN: NoteEditorPlugin = {
  type: NoteConstants.TABLE,
  typeClass: 'is-captionedTable',
  title: 'Table',
  icon: 'protocol-table',
  isCollapsible: () => true,
  canAutoCollapse: true,
  component: MediocrePlugins.captionWrapper<NoteEditor, Note, NotePart, TableItemProps>(
    {
      ...MediocrePlugins.blockPluginBase,
      selectionInit: MediocrePlugins.table.selectionInit,
      required: MediocrePlugins.table.required,
      default(
        rows: number,
        cols: number,
        param?: {isWellPlate?: boolean}
      ): {text: string; formats: Format; table: Table; alignment: string} {
        if (param == null) {
          param = {};
        }
        const {isWellPlate} = param;
        const {text, formats} = TEXT_PLUGIN_COMPONENT_BASE.default.call(this);
        const table = MediocrePlugins.table.default.call(this, rows, cols, {isWellPlate});
        return {table, text, formats, alignment: 'center'};
      },
      getItemProps: MediocrePlugins.getTableItemProps,
      getIsCollapsed(
        item: NotePart & {table: Table},
        editorProps: NoteEditorProps,
        collapsedStateById: CollapsedTableState
      ) {
        const {rowCount, cellCount} = TableUtil.getTableDimensionsFromTable(item.table);
        return isTableCollapsed({
          collapseId: createCollapseId(editorProps.fileId, item.table.name),
          collapsedStateById,
          rowCount,
          cellCount,
          tableKind: null,
          autoCollapseLimit: BenchlingConfig.ELN_PLAIN_TABLE_ROW_LIMIT_FOR_AUTO_COLLAPSE,
        });
      },
      getVirtualizedHeight(item: NotePart, isCollapsed: boolean) {
        return getTableSkeletonHeight(item.table?.table.length ?? 0, isCollapsed) + TABLE_CAPTION_LINE_HEIGHT;
      },
      renderVirtualizedSkeleton(item: NotePart, height: number): JSX.Element {
        return <TableSkeleton height={height} bottomSpacer={TABLE_CAPTION_LINE_HEIGHT} />;
      },
      getVirtualizationId(item: NotePart & {table: Table}) {
        return item.table.name;
      },
      make(
        item: NotePart & {table: Table},
        index: number,
        editorProps: NoteEditorProps,
        itemProps: TableItemProps,
        searchResultProps: MediocreSearchResultProps,
        resizeProps: unknown /* Specified via captionWrapper, unused as tables aren't pixel resizable */
      ): JSX.Element {
        const cid = editorProps.getCid(item);
        return MediocrePlugins.makeTable.call(this, item, index, editorProps, itemProps, searchResultProps, {
          tableItemCid: CaptionEditableClass.getAttachmentCid(cid),
        });
      },
    },
    {tableMode: true}
  ),
  linkable: false,
  canPasteFromOtherTenant: true,
  itemToText(this: NoteEditor, item: NotePart & {table: Table}): string {
    return TableUtil.tableEditableToText(item.table, this.props.computedTables[item.table.name]);
  },
  itemToHTML(this: NoteEditor, $el: JQuery, item: NotePart & {table: Table}): JQuery {
    $el.append(TableUtil.tableEditableToHTML(item.table, this.props.computedTables[item.table.name]));
    return $el;
  },
  itemFromHTML($el: JQuery, item: NotePart & {table: Table}): NotePart {
    return item;
  },
  getSpreadsheetTableName(item: NotePart & {table: Table}): string {
    return item.table.name;
  },
  getContent({text, formats, table}: {text: string; formats?: Format; table: Table | null}): {
    text: string;
    formats: Format | undefined;
    table: Table | null;
  } {
    return {text, formats, table};
  },
  setContent(content: TableContent): Spec<NotePart> {
    return setTableContent(content);
  },
  getText({text, formats}: {text: string; formats: Format}): {text: string; formats: Format} {
    return {text, formats};
  },
  setText({text, formats}: {text: string; formats: Format}): UpdateSpec {
    return {$merge: {text, formats}};
  },
  getStoreDependenciesForSpreadsheetEval(item: NotePart & {table: Table}): StoreDependencyMap {
    return {};
  },
  *getSearchResults(
    item: NotePart,
    matcher: TextMatcher,
    searchOverFormulas: boolean,
    computedTables: OutputTableMap | null,
    isCollapsed: boolean
  ): Generator<MediocreSearchResult> {
    // Search over the table name
    yield* getSearchResultsForTableName(matcher, item.table!.name, item.cid);

    // Search over the table column names
    const columnNames = item.table!.columnLabels.map((label) => label ?? '');
    yield* getSearchResultsForColumnNames(matcher, columnNames, item.cid);

    const computedTable = computedTables?.[item.table!.name];
    if (!isCollapsed) {
      assert(computedTable != null, 'Computed table not found in plugin', {
        computedTableNames: computedTables ? Object.keys(computedTables) : [],
      });
      for (const [i, row] of item.table!.table.entries()) {
        for (const [j, cell] of row.entries()) {
          if (doesTableCellMatch(matcher, cell, computedTable[i][j], searchOverFormulas)) {
            yield buildTableSearchResult(item.cid, i, j);
          }
        }
      }
    }

    // Search over the table's caption (`item.text`)
    for (const result of TEXT_PLUGIN.getSearchResults!(item, matcher)) {
      yield result;
    }
  },
};

export default TABLE_PLUGIN;

export function setTableContent(content: TableContent): Spec<NotePart> {
  return {$merge: pick(content, 'text', 'table', 'alignment', 'formats')};
}
